/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import {
    MarkEmailRead,
    ErrorOutline,
    Handshake,
    HeartBroken,
    Payments,
    Gavel,
    SwapHoriz,
    ChatBubbleOutline,
    PersonSearch,
    NotificationsOff,
    NotificationsActive,
    InsightsOutlined,
    AssignmentTurnedIn,
    AssignmentLate,
    FactCheck,
    History,
    AutoAwesomeOutlined,
} from "@mui/icons-material";

import { CollectSectionCard } from "../styles";
import { colors } from "../../../utility/types/constant";
import { IActivityFeedItem } from "../../../utility/types/collectTypes";
import { submitGetActivityFeed } from "../../../redux/services/collectService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    businessId: string | null;
    refreshKey?: number;
}

function timeAgo(value?: string | null): string {
    if (!value) return "—";
    const diff = Date.now() - new Date(value).getTime();
    if (diff < 0) return "just now";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

// Matches an ISO date (date-only or full datetime) anywhere in a string.
const ISO_DATE_RE =
    /\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)?/g;

/**
 * Turn machine-readable detail text into something friendlier. Currently this
 * rewrites any embedded ISO dates (e.g. dueDate=2026-07-03T00:00:00.000Z) into
 * a readable calendar date (Jul 3, 2026). Dates are formatted in UTC because
 * business dates like dueDate are stored at UTC midnight — formatting in the
 * viewer's local zone would shift them to the previous day.
 */
function humanizeDescription(text?: string | null): string {
    if (!text) return "";
    return text.replace(ISO_DATE_RE, (match) => {
        const d = new Date(match);
        if (Number.isNaN(d.getTime())) return match;
        return d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: "UTC",
        });
    });
}


const SEVERITY_COLOR: Record<string, string> = {
    info: "#0e7490",
    success: "#15803d",
    warning: "#b45309",
    critical: "#b91c1c",
};

/** Map an activity type to an icon (falls back to a neutral history icon). */
function iconForType(type: string): React.ReactNode {
    const map: Record<string, React.ReactNode> = {
        reminder_sent: <MarkEmailRead fontSize="small" />,
        reminder_failed: <ErrorOutline fontSize="small" />,
        reminder_skipped: <NotificationsOff fontSize="small" />,
        reply_received: <ChatBubbleOutline fontSize="small" />,
        reply_classified: <ChatBubbleOutline fontSize="small" />,
        promise_created: <Handshake fontSize="small" />,
        promise_kept: <Handshake fontSize="small" />,
        promise_broken: <HeartBroken fontSize="small" />,
        promise_cancelled: <HeartBroken fontSize="small" />,
        payment_received: <Payments fontSize="small" />,
        payment_partial: <Payments fontSize="small" />,
        manual_review_flagged: <AssignmentLate fontSize="small" />,
        reviewer_assigned: <PersonSearch fontSize="small" />,
        reviewer_unassigned: <PersonSearch fontSize="small" />,
        task_created: <AssignmentLate fontSize="small" />,
        task_completed: <AssignmentTurnedIn fontSize="small" />,
        task_cancelled: <AssignmentTurnedIn fontSize="small" />,
        state_transition: <SwapHoriz fontSize="small" />,
        pay_page_viewed: <FactCheck fontSize="small" />,
        pay_page_clicked: <FactCheck fontSize="small" />,
        customer_opted_out: <NotificationsOff fontSize="small" />,
        customer_opted_in: <NotificationsActive fontSize="small" />,
        prediction_generated: <InsightsOutlined fontSize="small" />,
        prediction_scenario_simulated: <InsightsOutlined fontSize="small" />,
        prediction_applied: <InsightsOutlined fontSize="small" />,
        legal_action: <Gavel fontSize="small" />,
    };
    return map[type] || <History fontSize="small" />;
}

/** Short "who did this" label. */
function actorLabel(item: IActivityFeedItem): string {
    if (item.actorType === "user")
        return item.actorDisplayName || "A teammate";
    if (item.actorType === "customer") return "Customer";
    if (item.actorType === "ai") return "FundsyFi AI";
    if (item.actorType === "webhook") return "QuickBooks";
    return "FundsyFi";
}

const PAGE_SIZE = 15;

const ActivityFeedPanel: React.FC<Props> = ({ businessId, refreshKey }) => {
    const navigate = useNavigate();
    const [items, setItems] = useState<IActivityFeedItem[]>([]);
    const [nextBefore, setNextBefore] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const requestSequence = useRef(0);

    const loadInitial = useCallback(async () => {
        const requestId = ++requestSequence.current;
        if (!businessId) {
            setItems([]);
            setNextBefore(null);
            setLoading(false);
            setLoadingMore(false);
            return;
        }
        setLoading(true);
        setLoadingMore(false);
        setError("");
        setItems([]);
        setNextBefore(null);
        try {
            const page = await submitGetActivityFeed(businessId, PAGE_SIZE);
            if (requestId === requestSequence.current) {
                setItems(page.items || []);
                setNextBefore(page.nextBefore);
            }
        } catch (e: any) {
            if (requestId === requestSequence.current) {
                setError(e?.message || "Failed to load activity.");
            }
        } finally {
            if (requestId === requestSequence.current) setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        loadInitial();
        return () => {
            requestSequence.current += 1;
        };
    }, [loadInitial, refreshKey]);

    const loadMore = async () => {
        if (!businessId || !nextBefore) return;
        const requestId = ++requestSequence.current;
        setLoadingMore(true);
        try {
            const page = await submitGetActivityFeed(
                businessId,
                PAGE_SIZE,
                nextBefore,
            );
            if (requestId === requestSequence.current) {
                setItems((prev) => [...prev, ...(page.items || [])]);
                setNextBefore(page.nextBefore);
            }
        } catch {
            // keep the existing list on a load-more failure
        } finally {
            if (requestId === requestSequence.current) setLoadingMore(false);
        }
    };

    if (!businessId) return null;

    return (
        <CollectSectionCard>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <AutoAwesomeOutlined sx={{ color: colors.primary }} />
                <Typography
                    sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.05rem", color: "#111827" }}
                >
                    What we did for you
                </Typography>
            </Box>
            <Typography
                sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#6b7280", mb: 1.75 }}
            >
                Every action the platform has taken on your behalf.
            </Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
            ) : error ? (
                <Typography
                    sx={{ fontFamily: FONT, fontSize: "0.82rem", color: "#b91c1c", py: 2 }}
                >
                    {error}
                </Typography>
            ) : items.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <History sx={{ fontSize: "2rem", color: "#cbd5e1", mb: 1 }} />
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.86rem", color: "#6b7280", fontWeight: 600 }}
                    >
                        No activity yet
                    </Typography>
                    <Typography
                        sx={{ fontFamily: FONT, fontSize: "0.76rem", color: "#9ca3af" }}
                    >
                        As soon as we send reminders or log customer replies, you'll see them here.
                    </Typography>
                </Box>
            ) : (
                <Box>
                    {items.map((item, idx) => {
                        const color = SEVERITY_COLOR[item.severity] || "#0e7490";
                        const clickable = Boolean(item.invoiceId);
                        return (
                            <Box
                                key={item._id}
                                onClick={
                                    clickable
                                        ? () =>
                                            navigate(
                                                `/collect/invoices/${item.invoiceId}`,
                                            )
                                        : undefined
                                }
                                sx={{
                                    display: "flex",
                                    gap: 1.25,
                                    py: 1.1,
                                    borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                                    cursor: clickable ? "pointer" : "default",
                                    "&:hover": clickable
                                        ? { backgroundColor: "#f9fafb" }
                                        : {},
                                    px: 0.5,
                                    borderRadius: "8px",
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        backgroundColor: `${color}1a`,
                                        color,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mt: 0.25,
                                    }}
                                >
                                    {iconForType(item.type)}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            justifyContent: "space-between",
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: FONT,
                                                fontSize: "0.84rem",
                                                fontWeight: 700,
                                                color: "#111827",
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT,
                                                fontSize: "0.7rem",
                                                color: "#9ca3af",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {timeAgo(item.occurredAt)}
                                        </Typography>
                                    </Box>
                                    {item.description && (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT,
                                                fontSize: "0.76rem",
                                                color: "#6b7280",
                                                mt: 0.25,
                                            }}
                                        >
                                            {humanizeDescription(
                                                item.description,
                                            )}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={actorLabel(item)}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            height: 18,
                                            fontFamily: FONT,
                                            fontSize: "0.62rem",
                                            fontWeight: 600,
                                            backgroundColor: "#f3f4f6",
                                            color: "#6b7280",
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    })}

                    {nextBefore && (
                        <Box sx={{ textAlign: "center", pt: 1.5 }}>
                            <Typography
                                onClick={loadMore}
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    color: colors.primary,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    "&:hover": { textDecoration: "underline" },
                                }}
                            >
                                {loadingMore ? (
                                    <CircularProgress size={14} sx={{ color: colors.primary }} />
                                ) : (
                                    "Show more"
                                )}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default ActivityFeedPanel;
